import { DealerSpaceParameterKey } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import {
  defineSpaceParameterPanelValues,
  SPACE_DATE_RANGE_PANEL_VALUES,
  useSpaceParameterizedFilter,
} from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartiesFilter } from '@src/hooks/filters/useCounterpartyFilter';
import { useMarketAccountsFilter } from '@src/hooks/filters/useMarketAccountsFilter';
import {
  AssetTransactionTypeEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  useAssetsFilter,
  useGenericFilter,
  type AssetTransaction,
  type DateRangeFilter,
  type FilterableProperty,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, pick } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

export interface CustomerAssetTransactionsFilter extends DateRangeFilter {
  Counterparties?: string[];
  MarketAccounts?: string[];
  Symbols?: string[];
  PositionAssets?: string[];
  HideAPICalls?: boolean;
}

export interface UseCustomerAssetTransactionsFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.DateRange]: SPACE_DATE_RANGE_PANEL_VALUES,
  [DealerSpaceParameterKey.MarketAccounts]: 'MarketAccounts',
});
export const CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_KEYS =
  CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT.keys;

export function isIncludedCustomerAssetTransaction(entity: AssetTransaction) {
  return (
    entity.Type === AssetTransactionTypeEnum.RealizedPnL ||
    entity.Type === AssetTransactionTypeEnum.Funding ||
    entity.Type === AssetTransactionTypeEnum.Delivery
  );
}

export function useCustomerAssetTransactionsFilterableProperties() {
  const counterpartiesFilter = useCounterpartiesFilter({ label: 'Counterparty' });
  const marketAccountsFilter = useMarketAccountsFilter({ label: 'Market Account' });
  const assetsFilter = useAssetsFilter();

  return useMemo(
    () =>
      compact<FilterableProperty<keyof CustomerAssetTransactionsFilter>>([
        counterpartiesFilter,
        marketAccountsFilter,
        { ...assetsFilter, field: 'Asset' },
        { ...assetsFilter, key: 'PositionAssets', field: 'PositionAsset', label: 'Position Asset' },
      ]),
    [counterpartiesFilter, marketAccountsFilter, assetsFilter]
  );
}

export function useCustomerAssetTransactionsFilter({
  persistedBlotterTable,
}: UseCustomerAssetTransactionsFilterParams) {
  const [initialFilter] = useState<CustomerAssetTransactionsFilter>(
    () => cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter) as CustomerAssetTransactionsFilter
  );
  const filterableProperties = useCustomerAssetTransactionsFilterableProperties();
  const localFilterResults = useGenericFilter<CustomerAssetTransactionsFilter>(filterableProperties, {
    initialFilter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
  });
  const { changeFilter, controlledKeys, filter, getSelfManagedFilterBuilderProps } = useSpaceParameterizedFilter({
    filter: localFilterResults.filter,
    changeFilter: localFilterResults.changeFilter,
    spaceParameterPanelValues: CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
  });
  const clientSideFilter = useCallback(
    ({ data }: { data?: AssetTransaction }) => {
      if (data == null || !isIncludedCustomerAssetTransaction(data)) {
        return false;
      }
      return (
        !filterExistsAndExcludes(filter, 'MarketAccounts', data, 'MarketAccount') &&
        !filterExistsAndExcludes(filter, 'Symbols', data, 'Asset') &&
        !filterExistsAndExcludes(filter, 'PositionAssets', data, 'PositionAsset')
      );
    },
    [filter]
  );
  const filterBuilderProps = useMemo(
    () => getSelfManagedFilterBuilderProps(localFilterResults.filterBuilderProps),
    [getSelfManagedFilterBuilderProps, localFilterResults.filterBuilderProps]
  );
  const blotterTableFilterProps = useMemo(
    () =>
      controlledKeys.includes(DealerSpaceParameterKey.DateRange)
        ? { showDateRangePicker: false }
        : localFilterResults.blotterTableFilterProps,
    [controlledKeys, localFilterResults.blotterTableFilterProps]
  );

  return {
    initialFilter,
    ...localFilterResults,
    filter,
    changeFilter,
    clientSideFilter,
    contextMenuFilterableProperties: filterableProperties,
    filterBuilderProps,
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  MarketAccount: 'MarketAccounts',
  Asset: 'Symbols',
  PositionAsset: 'PositionAssets',
} satisfies Partial<Record<keyof AssetTransaction, keyof CustomerAssetTransactionsFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof CustomerAssetTransactionsFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof CustomerAssetTransactionsFilter
    | undefined;
}

export function onlyServerFilterKeys(filter: CustomerAssetTransactionsFilter | undefined) {
  if (!filter) {
    return filter;
  }

  return pick({ ...filter }, ['StartDate', 'EndDate', 'Counterparties', 'MarketAccounts', 'Symbols', 'PositionAssets']);
}
